import React, { useEffect, useRef } from 'react'
import { Col, Form, Input as InputAntd } from 'antd'
import { Formik } from 'formik'
import produce from 'immer'
import { Marker } from 'react-google-maps'

import { Button } from 'src/components/Ant'
import useStandardResource from 'src/core/hooks/use-standard-resource'
import { RequestItinerary } from 'src/types'
import MapContainer from 'src/components/MapContainer'
import {
  useGetInitialValues,
  getValidationSchema,
  validate
} from 'src/core/forms/request/itinerary'
import { useStepsContext } from 'src/views/Requests/views/Form'

import { WrapCard, WrapRow } from '../styles'
import StepsNavigation from '../../StepsNavigation'

import { WrapButtons } from './styles'
import FormFields from './components/FormFields'
import ItinerariesSummary from './components/ItinerariesSummary'

function Itineraries(): JSX.Element {
  const stepsContext = useStepsContext()
  const refActivityInput = useRef<InputAntd>(null)

  const refFormik = useRef(null)
  const refMap = useRef(null)

  const initialValues = useGetInitialValues()
  const referenceStandard = useStandardResource('references')

  useEffect(() => {
    const map =
      refMap.current && refMap.current.state && refMap.current.state.map

    if (map) {
      // @ts-ignore
      const bounds = new window.google.maps.LatLngBounds()

      stepsContext.request.request_itineraries.forEach(
        ({ latitude, longitude }) => {
          // @ts-ignore
          bounds.extend(new window.google.maps.LatLng(latitude, longitude))
        }
      )

      map.fitBounds(bounds, 50)
    }
  }, [stepsContext.request.request_itineraries])

  function handleResetForm() {
    setTimeout(() => {
      refFormik.current?.resetForm({
        ...initialValues,
        reference_id: referenceStandard
          ? referenceStandard.reference_id.toString()
          : null,
        reference: referenceStandard
      })
    }, 100)
  }

  return (
    <Formik
      ref={refFormik}
      initialValues={initialValues}
      onSubmit={values => {
        stepsContext.setRequest(
          produce(stepsContext.request, draft => {
            if (values.request_itinerary_id || values.tmp_id) {
              draft.request_itineraries = draft.request_itineraries.map(
                editedItem => ({
                  ...editedItem,
                  ...(editedItem.tmp_id === values.tmp_id &&
                  editedItem.request_itinerary_id ===
                    values.request_itinerary_id
                    ? values
                    : {})
                })
              )
            } else {
              draft.request_itineraries.push({
                ...values,
                ...(values.request_itinerary_id
                  ? {}
                  : { tmp_id: Math.random() })
              })
            }
          })
        )

        handleResetForm()

        refActivityInput.current.focus()
      }}
      validate={values => validate({ request: stepsContext.request, values })}
      validationSchema={getValidationSchema()}
      render={formikProps => {
        const isTempId =
          !!formikProps.values.request_itinerary_id ||
          !!formikProps.values.tmp_id

        return (
          <Form onSubmit={formikProps.handleSubmit}>
            <StepsNavigation />
            <WrapRow>
              <Col span={15}>
                <WrapCard
                  data-test-id="itinerary-card"
                  bodyStyle={{ minHeight: 320 }}
                >
                  <FormFields
                    organization_id={
                      stepsContext.request.organization_id ||
                      stepsContext.request.request_user_data[0]?.reason
                        ?.organization_id
                    }
                    formikProps={formikProps}
                    refActivityInput={refActivityInput}
                  />
                  <Form.Item>
                    <WrapButtons>
                      <div>
                        <Button
                          block
                          data-test-id="itinerary-action"
                          htmlType="submit"
                          icon={isTempId ? 'edit' : ''}
                          title={
                            isTempId
                              ? 'common.actions.change'
                              : 'common.actions.save'
                          }
                        />
                      </div>
                      {isTempId && (
                        <div>
                          <Button
                            block
                            onClick={() => handleResetForm()}
                            title="common.actions.cancel-edit"
                            type="dashed"
                          />
                        </div>
                      )}
                    </WrapButtons>
                  </Form.Item>
                </WrapCard>
              </Col>
              <Col offset={1} span={8}>
                <ItinerariesSummary
                  bodyStyle={{ height: 320, overflow: 'auto' }}
                  handleDelete={(itemDelete: RequestItinerary) => {
                    stepsContext.setRequest(
                      produce(stepsContext.request, draft => {
                        if (itemDelete.request_itinerary_id) {
                          draft.request_itineraries.forEach(
                            deletedItem =>
                              (deletedItem.active =
                                deletedItem.request_itinerary_id ===
                                itemDelete.request_itinerary_id
                                  ? false
                                  : itemDelete.active)
                          )
                        } else if (itemDelete.tmp_id) {
                          draft.request_itineraries =
                            draft.request_itineraries.filter(
                              deletedItem =>
                                deletedItem.tmp_id !== itemDelete.tmp_id
                            )
                        }
                      })
                    )

                    handleResetForm()
                  }}
                  handleEdit={(itemEdit: RequestItinerary) =>
                    formikProps.resetForm(itemEdit)
                  }
                  request={stepsContext.request}
                />
              </Col>
            </WrapRow>
            <WrapRow>
              <Col span={24}>
                <WrapCard bodyStyle={{ height: 400 }}>
                  <MapContainer ref={refMap}>
                    {stepsContext.request.request_itineraries.map(item => (
                      <Marker
                        key={item.request_id || item.tmp_id}
                        position={{ lat: item.latitude, lng: item.longitude }}
                      />
                    ))}
                  </MapContainer>
                </WrapCard>
              </Col>
            </WrapRow>
          </Form>
        )
      }}
    />
  )
}

export default Itineraries
